import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";

// Components
import HeroSection from "../components/landing/HeroSection";
import StorySection from "../components/landing/StorySection";
import ProductsSection from "../components/landing/ProductsSection";
import BoxBuilderSection from "../components/landing/BoxBuilderSection";
import QuizSection from "../components/landing/QuizSection";
import SpinWheelSection from "../components/landing/SpinWheelSection";
import GiftSection from "../components/landing/GiftSection";
import TestimonialsSection from "../components/landing/TestimonialsSection";
import LivePollSection from "../components/landing/LivePollSection";
import GallerySection from "../components/landing/GallerySection";
import WhatsAppChatbot from "../components/landing/WhatsAppChatbot";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";

const LandingPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  // Scroll spy for navigation
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "productos", "arma-tu-caja", "quiz", "ruleta", "regala", "testimonios"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const addToCart = (item) => {
    setCartItems((prev) => [...prev, item]);
    toast.success(`${item.name} agregado a tu pedido`);
    
    // Small confetti burst
    confetti({
      particleCount: 30,
      spread: 60,
      origin: { y: 0.8 },
      colors: ["#C88037", "#2D1810", "#E5984A"],
    });
  };

  const removeFromCart = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
    toast.info("Producto eliminado");
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price || item.basePrice || 0), 0);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      {/* Navigation */}
      <Navbar 
        activeSection={activeSection} 
        cartCount={cartItems.length}
        onCartClick={() => setShowCart(true)}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Story Section */}
      <StorySection />

      {/* Products Section */}
      <ProductsSection onAddToCart={addToCart} />

      {/* Box Builder - Drag & Drop */}
      <BoxBuilderSection onAddToCart={addToCart} />

      {/* Quiz Section */}
      <QuizSection />

      {/* Spin Wheel Section */}
      <SpinWheelSection />

      {/* Gift Section */}
      <GiftSection onAddToCart={addToCart} />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* Live Poll */}
      <LivePollSection />

      {/* Gallery */}
      <GallerySection />

      {/* Footer */}
      <Footer />

      {/* WhatsApp Chatbot */}
      <WhatsAppChatbot 
        cartItems={cartItems} 
        cartTotal={getCartTotal()}
        onClearCart={clearCart}
      />

      {/* Cart Drawer */}
      <AnimatePresence>
        {showCart && (
          <CartDrawer
            items={cartItems}
            total={getCartTotal()}
            onClose={() => setShowCart(false)}
            onRemove={removeFromCart}
            onClear={clearCart}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Cart Drawer Component
const CartDrawer = ({ items, total, onClose, onRemove, onClear }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const generateWhatsAppMessage = () => {
    let message = "¡Hola Urban Brownies! 🍫\n\nQuiero hacer el siguiente pedido:\n\n";
    items.forEach((item, index) => {
      message += `${index + 1}. ${item.name} - ${formatPrice(item.price || item.basePrice)}\n`;
    });
    message += `\n💰 Total: ${formatPrice(total)}\n\n¿Está disponible para hoy?`;
    return encodeURIComponent(message);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-[#FDFBF7] z-50 shadow-2xl"
        data-testid="cart-drawer"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#E5DCD0]">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-serif text-[#2D1810]">Tu Pedido</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[#F4EDE4] rounded-full transition-colors"
                data-testid="close-cart"
              >
                <svg className="w-6 h-6 text-[#5E4B3C]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🍫</div>
                <p className="text-[#5E4B3C]">Tu carrito está vacío</p>
                <p className="text-sm text-[#5E4B3C]/70 mt-2">¡Agrega algunos brownies deliciosos!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-4 p-4 bg-[#F4EDE4] rounded-xl"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-[#2D1810]">{item.name}</h4>
                      <p className="text-sm text-[#C88037] font-semibold">
                        {formatPrice(item.price || item.basePrice)}
                      </p>
                    </div>
                    <button
                      onClick={() => onRemove(index)}
                      className="p-2 hover:bg-[#E5DCD0] rounded-full transition-colors"
                      data-testid={`remove-item-${index}`}
                    >
                      <svg className="w-5 h-5 text-[#D9381E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="p-6 border-t border-[#E5DCD0] bg-[#F4EDE4]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[#5E4B3C]">Total:</span>
                <span className="text-2xl font-bold text-[#2D1810]">{formatPrice(total)}</span>
              </div>
              <div className="space-y-3">
                <a
                  href={`https://wa.me/573107544509?text=${generateWhatsAppMessage()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#25D366] text-white text-center py-4 rounded-full font-semibold hover:bg-[#20BD5A] transition-colors"
                  data-testid="checkout-whatsapp"
                >
                  Pedir por WhatsApp
                </a>
                <button
                  onClick={onClear}
                  className="w-full text-[#5E4B3C] py-2 hover:text-[#D9381E] transition-colors text-sm"
                  data-testid="clear-cart"
                >
                  Vaciar carrito
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default LandingPage;
